// Skin Lab: what the page needs to turn a Unity skin export (manifest.xml +
// originals/*.png, the "Export for Backend" zip) into gpt-image-2 calls and
// back into PNGs the Unity import accepts. The spec is the backend ТЗ
// "генерация ассетов под тему билда (777-Slot-Games, лобби)".

export interface SkinSprite { x: number; y: number; width: number; height: number }

export interface SkinAsset {
  id: string;
  category: string;
  width: number;
  height: number;
  alpha: boolean;
  /** Sprite-sheet rectangles, origin bottom-left; empty for a single picture. */
  sprites: SkinSprite[];
  description: string;
  notes: string;
  /** Path of the original inside the zip. */
  original: string;
}

export interface SkinManifest {
  game: string;
  scope: string;
  genre: string;
  screen: string;
  orientation: string;
  currentStyle: string;
  assets: SkinAsset[];
}

const num = (v: string | null) => Number(v ?? '0') || 0;

export function parseSkinManifest(xml: string): SkinManifest {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('manifest.xml is not valid XML');
  const root = doc.documentElement;
  if (root.tagName !== 'skinManifest') throw new Error('manifest.xml has no <skinManifest> root');
  const ctx = root.getElementsByTagName('context')[0];
  const assets: SkinAsset[] = [];
  for (const el of Array.from(root.getElementsByTagName('asset'))) {
    const file = el.getElementsByTagName('file')[0];
    if (!file) continue;
    const sprites = Array.from(el.getElementsByTagName('sprite')).map((s) => ({
      x: num(s.getAttribute('x')), y: num(s.getAttribute('y')),
      width: num(s.getAttribute('width')), height: num(s.getAttribute('height')),
    }));
    assets.push({
      id: el.getAttribute('id') ?? '',
      category: el.getAttribute('category') ?? '',
      width: num(file.getAttribute('width')),
      height: num(file.getAttribute('height')),
      alpha: file.getAttribute('alpha') === 'true',
      sprites,
      description: el.getElementsByTagName('description')[0]?.textContent?.trim() ?? '',
      notes: el.getElementsByTagName('notes')[0]?.textContent?.trim() ?? '',
      original: file.getAttribute('original') ?? `originals/${el.getAttribute('id')}.png`,
    });
  }
  return {
    game: root.getAttribute('game') ?? '',
    scope: root.getAttribute('scope') ?? '',
    genre: ctx?.getAttribute('genre') ?? 'casino slot machine',
    screen: ctx?.getAttribute('screen') ?? 'lobby',
    orientation: ctx?.getAttribute('orientation') ?? 'landscape',
    currentStyle: ctx?.getElementsByTagName('currentStyle')[0]?.textContent?.trim() ?? '',
    assets: assets.filter((a) => a.id && a.width > 0 && a.height > 0),
  };
}

// gpt-image-2's size rules: both edges multiples of 16, the long edge at most
// 3840 and at most 3× the short one, 655,360–8,294,400 pixels in all.
const SIZE_STEP = 16;
const MAX_EDGE = 3840;
const MAX_RATIO = 3;
export const MIN_DRAW_PIXELS = 655_360;

const round16 = (v: number) => Math.max(SIZE_STEP, Math.round(v / SIZE_STEP) * SIZE_STEP);

/**
 * The size to draw an asset at: its own aspect (clamped to 3:1, so a thin bar
 * is drawn squashed and stretched back), its own pixel count kept between the
 * model's minimum and `maxPixels`, which caps what a call costs.
 */
export function drawSize(width: number, height: number, maxPixels: number): { width: number; height: number; stretched: number } {
  const ratio = Math.min(MAX_RATIO, Math.max(1 / MAX_RATIO, width / height));
  const pixels = Math.min(Math.max(width * height, MIN_DRAW_PIXELS), Math.max(maxPixels, MIN_DRAW_PIXELS));
  let w = Math.min(MAX_EDGE, round16(Math.sqrt(pixels * ratio)));
  let h = Math.min(MAX_EDGE, round16(w / ratio));
  while (w * h < MIN_DRAW_PIXELS) {
    if (ratio >= 1) { w += SIZE_STEP; h = round16(w / ratio); } else { h += SIZE_STEP; w = round16(h * ratio); }
  }
  // How much the final picture is stretched against the drawing (1 = not at all).
  const stretched = (width / height) / (w / h);
  return { width: w, height: h, stretched };
}

/** The sets the spec wants to look like one family. */
const GROUPS: { name: string; ids: string[] }[] = [
  { name: 'the lobby buttons', ids: ['lobby.button.menu', 'lobby.button.dailyspin', 'lobby.button.shop', 'lobby.button.deal'] },
  { name: 'the top header', ids: ['lobby.header.bar', 'lobby.header.field', 'lobby.header.levelbar', 'lobby.header.coin', 'lobby.header.star'] },
  { name: 'the promo slider', ids: ['lobby.deal.card', 'lobby.deal.slide2', 'lobby.deal.slide3', 'lobby.deal.dot.empty', 'lobby.deal.dot.full'] },
  { name: 'the coins', ids: ['lobby.header.coin', 'lobby.deco.coin.crown', 'lobby.deco.coin.dollar', 'lobby.deco.coinpiles'] },
];

/** Ids whose originals carry a big headline the new art should carry too. */
const HEADLINE_IDS = new Set(['lobby.deal.slide2', 'lobby.deal.slide3']);

/** Asset-specific rules from the spec. */
const EXTRA_RULES: Record<string, string> = {
  'lobby.header.field': 'An EMPTY plate for the balance and level: no digits, no text, no icons on it.',
  'lobby.header.levelbar': 'The FILL of a progress bar: a glossy bar that still looks natural when the game cuts it off on the right at any length. No digits, no text.',
};

export function isStyleAnchor(a: SkinAsset): boolean {
  return a.category === 'background';
}

/** The prompt for one asset; `withStyleRef` when a second, style image is sent. */
export function buildSkinPrompt(m: SkinManifest, a: SkinAsset, theme: string, opts: { withStyleRef: boolean; stretched: number }): string {
  const groups = GROUPS.filter((g) => g.ids.includes(a.id)).map((g) => g.name);
  const sheet = a.sprites.length > 1;
  const lines: string[] = [];
  lines.push(`Re-skin one UI image of a mobile ${m.genre} game (${m.screen} screen, ${m.orientation}) in a new theme. Every image of the set is redrawn in this theme and they must look like the work of one artist.`);
  lines.push('');
  lines.push(`THEME: ${theme.trim()}`);
  if (m.currentStyle) lines.push(`The current style is "${m.currentStyle}" — REPLACE it; do not keep its colours, materials or neon.`);
  lines.push('');
  lines.push(`IMAGE 1 is the current ${a.category} image. Use it ONLY for shape: keep its silhouette, proportions, composition and where every part sits on the canvas, so the game's text and overlays still land in the right places. Take nothing of its colours or style.`);
  if (opts.withStyleRef) {
    lines.push('IMAGE 2 is the new lobby background, already drawn in the theme. Match its palette, lighting, materials and rendering style exactly; do NOT copy its scene into this image.');
  } else if (isStyleAnchor(a)) {
    lines.push('This background is drawn first and sets the style for the whole set: rich, premium mobile-casino quality, a clear and confident palette.');
  }
  lines.push('');
  lines.push(`WHAT THIS IMAGE IS (${a.id}): ${a.description}`);
  if (a.notes) lines.push(`Developer's note (overrides the description): ${a.notes}`);
  if (groups.length) lines.push(`It belongs with ${groups.join(' and ')}: draw it as part of that family.`);
  if (EXTRA_RULES[a.id]) lines.push(EXTRA_RULES[a.id]!);
  if (a.category === 'button') lines.push('It must read as a pressable button: volume, bevel, a clear frame.');
  if (Math.abs(opts.stretched - 1) > 0.15) {
    lines.push(opts.stretched > 1
      ? `The canvas is narrower than the real image: it will be stretched ${opts.stretched.toFixed(1)}× wider, so keep the design simple and horizontally even — no circles, faces or symbols that would look distorted.`
      : `The canvas is shorter than the real image: it will be stretched ${(1 / opts.stretched).toFixed(1)}× taller, so keep the design simple and vertically even — no circles, faces or symbols that would look distorted.`);
  }
  lines.push('');
  lines.push('RULES (hard):');
  lines.push('- Where the description says text or other images are drawn on top, keep that area calm, plain and dark enough for white text.');
  lines.push(HEADLINE_IDS.has(a.id)
    ? '- If image 1 carries a big headline, draw a short headline in the same place, in correct English with clean letters; no other text.'
    : '- No text, letters or numbers anywhere.');
  lines.push('- No logos, brands or copies of existing games\' art (including PG Soft\'s Fortune Tiger); no watermark; no border, frame or margin the original does not have.');
  if (a.alpha) {
    lines.push(sheet
      ? `- Image 1 is a sprite sheet: ${a.sprites.length} separate small pictures on magenta. Draw the same number of separate pictures in the same spots, each inside its own spot, never touching its neighbours.`
      : '- One object only, in the same spot and size as in image 1.');
    lines.push('- BACKGROUND (critical): perfectly flat, solid pure magenta #FF00FF wherever image 1 is magenta — no gradient, no floor, no cast shadow, no glow or sparkles on the background.');
    lines.push('- The object itself contains NO magenta, pink or purple.');
  } else {
    lines.push('- Opaque and full-bleed: the art fills the whole canvas edge to edge.');
  }
  return lines.join('\n');
}

/**
 * gpt-image-2 cannot draw transparency, so transparent assets are drawn on
 * flat magenta and keyed here — the same key as the loader icon
 * (api/cloudflare/src/lib/loader-image.ts keyOutMagenta), in place on RGBA.
 */
export function keyOutMagenta(rgba: Uint8ClampedArray): void {
  const LO = 70, HI = 170;
  for (let i = 0; i < rgba.length; i += 4) {
    const r = rgba[i]!, g = rgba[i + 1]!, b = rgba[i + 2]!;
    const m = Math.min(r, b) - g;
    const alpha = m >= HI ? 0 : m <= LO ? 1 : (HI - m) / (HI - LO);
    if (alpha === 1) continue;
    if (alpha === 0) { rgba[i] = rgba[i + 1] = rgba[i + 2] = rgba[i + 3] = 0; continue; }
    const unmix = (c: number, k: number) => (c - (1 - alpha) * k) / alpha;
    rgba[i] = unmix(r, 255);
    rgba[i + 1] = unmix(g, 0);
    rgba[i + 2] = unmix(b, 255);
    rgba[i + 3] = alpha * rgba[i + 3]!;
  }
}

/** Clear every pixel outside the sprite rectangles (origin bottom-left). */
export function clearOutsideSprites(rgba: Uint8ClampedArray, width: number, height: number, sprites: SkinSprite[]): void {
  const inside = new Uint8Array(width * height);
  for (const s of sprites) {
    const top = height - (s.y + s.height);
    for (let y = Math.max(0, top); y < Math.min(height, top + s.height); y++) {
      inside.fill(1, y * width + Math.max(0, s.x), y * width + Math.min(width, s.x + s.width));
    }
  }
  for (let p = 0; p < inside.length; p++) if (!inside[p]) rgba[p * 4 + 3] = 0;
}

/** USD per 1M tokens; editable on the page since OpenAI changes them. */
export interface ImagePrices { textIn: number; imageIn: number; out: number }
export const DEFAULT_IMAGE_PRICES: ImagePrices = { textIn: 5, imageIn: 8, out: 30 };

export interface ImageUsage {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  input_tokens_details?: { text_tokens?: number; image_tokens?: number };
}

/** What one call cost, or null when OpenAI sent no usage. */
export function usageCost(u: ImageUsage | null, p: ImagePrices): number | null {
  if (!u) return null;
  const text = u.input_tokens_details?.text_tokens;
  const image = u.input_tokens_details?.image_tokens;
  const input = text != null || image != null
    ? (text ?? 0) * p.textIn + (image ?? 0) * p.imageIn
    : (u.input_tokens ?? 0) * p.imageIn;
  return (input + (u.output_tokens ?? 0) * p.out) / 1_000_000;
}

export interface SkinCheck { ok: boolean; problems: string[] }

/** The spec's pre-send checks on one finished PNG's pixels. */
export function checkSkinOutput(a: SkinAsset, width: number, height: number, rgba: Uint8ClampedArray): SkinCheck {
  const problems: string[] = [];
  if (width !== a.width || height !== a.height) problems.push(`${width}×${height}, needs ${a.width}×${a.height}`);
  let clear = 0;
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i]! < 250) clear++;
  if (a.alpha && clear === 0) problems.push('no transparent pixels');
  if (!a.alpha && clear > 0) problems.push('has transparent pixels');
  return { ok: problems.length === 0, problems };
}
